// Sonner behavior port; see THIRD_PARTY_NOTICES.md in this directory.
import { DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Inject,
  OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ScenarioToast, ScenarioToastButton, ScenarioToastDrag, ScenarioToastRow } from '../../models/scenario-toast.model';
import { ScenarioToastService } from '../../services/scenario-toast.service';

@Component({
  selector: 'app-scenario-toaster',
  templateUrl: './scenario-toaster.component.html',
  styleUrls: ['./scenario-toaster.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScenarioToasterComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('list', { static: true }) private list?: ElementRef<HTMLOListElement>;
  readonly dots = Array.from({ length: 25 }, (_, index) => index);
  rows: ScenarioToastRow[] = [];
  expanded = false;
  held = false;
  frontHeight = '0px';
  private toasts: readonly ScenarioToast[] = [];
  private readonly heights = new Map<string, number>();
  private readonly offsets = new Map<string, number>();
  private readonly swipes = new Map<string, { direction: string; x: number; y: number }>();
  private readonly destroy$ = new Subject<void>();
  private hovered = false;
  private focused = false;
  private drag: ScenarioToastDrag | null = null;
  private frame: number | null = null;
  private focusBeforeHotkey: HTMLElement | null = null;
  private destroyed = false;
  private resizeObserver?: { observe(element: HTMLElement): void; disconnect(): void };

  constructor(private readonly toast: ScenarioToastService, private readonly cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document) { }

  ngOnInit(): void {
    this.toast.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      this.toasts = state.toasts;
      this.held = state.held;
      if (!state.toasts.length) {
        this.hovered = false;
        this.focused = false;
        this.expanded = false;
        this.toast.setPaused('expanded', false);
      }
      this.rebuild();
      this.scheduleMeasure();
    });
    this.visibilityChanged();
  }

  ngAfterViewInit(): void {
    // ResizeObserver is not present in the TypeScript 4.0 DOM declarations.
    const Observer = this.document.defaultView && (this.document.defaultView as any).ResizeObserver;
    if (Observer && this.list) {
      this.resizeObserver = new Observer(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.list.nativeElement);
    }
    this.scheduleMeasure();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    if (this.frame !== null) { this.document.defaultView?.cancelAnimationFrame(this.frame); }
    this.resizeObserver?.disconnect();
    this.toast.setPaused('expanded', false);
    this.toast.setPaused('pointer', false);
    this.toast.setPaused('hidden', false);
  }

  trackById(_index: number, row: ScenarioToastRow): string { return row.id; }
  trackByDot(_index: number, dot: number): number { return dot; }

  hover(hovered: boolean): void { this.hovered = hovered; this.syncExpansion(); }
  focusIn(): void { this.focused = true; this.syncExpansion(); }
  focusOut(event: FocusEvent): void {
    if (this.list?.nativeElement.contains(event.relatedTarget as Node)) { return; }
    this.focused = false;
    const trigger = this.focusBeforeHotkey;
    this.focusBeforeHotkey = null;
    trigger?.focus({ preventScroll: true });
    this.syncExpansion();
  }

  @HostListener('document:visibilitychange')
  visibilityChanged(): void { this.toast.setPaused('hidden', this.document.hidden); }

  @HostListener('document:keydown', ['$event'])
  keydown(event: KeyboardEvent): void {
    if (this.held || !this.rows.length) { return; }
    const target = event.target as Element;
    const editable = target?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"])');
    if (event.altKey && event.code === 'KeyT' && !editable) {
      event.preventDefault();
      this.focusBeforeHotkey = this.document.activeElement as HTMLElement;
      this.focused = true;
      this.syncExpansion();
      this.list?.nativeElement.focus({ preventScroll: true });
    }
    if (event.code === 'Escape' && this.list?.nativeElement.contains(this.document.activeElement)) {
      event.preventDefault();
      const trigger = this.focusBeforeHotkey;
      this.focusBeforeHotkey = null;
      trigger?.focus({ preventScroll: true });
      // Focus keeps timers paused while the user remains in the stack.
      this.expanded = false;
      this.rebuild();
    }
  }

  action(row: ScenarioToastRow, button: ScenarioToastButton): void {
    if (row.type === 'loading' || row.removed) { return; }
    button.onClick();
    this.toast.dismiss(row.id);
  }

  pointerDown(event: PointerEvent, row: ScenarioToastRow): void {
    if (event.button !== 0 || row.type === 'loading' || row.dismissible === false || row.removed ||
        (event.target as Element).closest('button') || this.document.defaultView?.getSelection()?.toString()) { return; }
    this.drag = { id: row.id, startX: event.clientX, startY: event.clientY, started: Date.now(), axis: null, x: 0, y: 0 };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.toast.setPaused('pointer', true);
    this.rebuild();
  }

  pointerMove(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || this.document.defaultView?.getSelection()?.toString()) { return; }
    const dx = event.clientX - drag.startX, dy = event.clientY - drag.startY;
    if (!drag.axis && (Math.abs(dx) > 1 || Math.abs(dy) > 1)) { drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'; }
    const delta = drag.axis === 'x' ? dx : dy;
    const amount = delta >= 0 ? delta : delta / (1.5 + Math.abs(delta) / 20);
    drag.x = drag.axis === 'x' ? amount : 0;
    drag.y = drag.axis === 'y' ? amount : 0;
    this.rebuild();
  }

  pointerUp(): void {
    const drag = this.drag;
    if (drag) {
      const amount = drag.axis === 'x' ? drag.x : drag.y;
      const velocity = amount / Math.max(1, Date.now() - drag.started);
      if (amount > 0 && (amount >= 45 || velocity > 0.11)) {
        this.swipes.set(drag.id, { direction: drag.axis === 'x' ? 'right' : 'down', x: drag.x, y: drag.y });
        this.toast.dismiss(drag.id);
      }
    }
    this.pointerCancel();
  }

  pointerCancel(): void {
    this.drag = null;
    this.toast.setPaused('pointer', false);
    this.rebuild();
  }

  private syncExpansion(): void {
    this.expanded = this.hovered || this.focused;
    this.toast.setPaused('expanded', this.expanded);
    this.rebuild();
  }

  private rebuild(): void {
    let offset = 0;
    this.rows = this.toasts.map((toast, index) => {
      const height = this.heights.get(toast.id) || 0;
      if (!toast.removed) { this.offsets.set(toast.id, offset); offset += height + 14; }
      const position = this.offsets.get(toast.id) || 0;
      const dragging = this.drag?.id === toast.id;
      const outgoing = this.swipes.get(toast.id);
      return { ...toast, index, front: index === 0, visible: index < 3, offset: position, height,
        offsetPx: `${position}px`, heightPx: `${height}px`, zIndex: this.toasts.length - index,
        swiping: dragging, swipeOut: outgoing?.direction || null,
        swipeX: `${dragging ? this.drag.x : outgoing?.x || 0}px`, swipeY: `${dragging ? this.drag.y : outgoing?.y || 0}px`,
        tabIndex: toast.announce && index < 3 && !toast.removed ? 0 : null };
    });
    const front = this.rows.find(row => !row.removed);
    this.frontHeight = `${front?.height || 0}px`;
    const live = new Set(this.toasts.map(toast => toast.id));
    const known = new Set([...this.heights.keys(), ...this.offsets.keys(), ...this.swipes.keys()]);
    known.forEach(id => {
      if (!live.has(id)) { this.heights.delete(id); this.offsets.delete(id); this.swipes.delete(id); }
    });
    this.cdr.markForCheck();
  }

  private scheduleMeasure(): void {
    const win = this.document.defaultView;
    if (this.destroyed || !win || !this.list || this.frame !== null || this.held) { return; }
    this.frame = win.requestAnimationFrame(() => {
      this.frame = null;
      if (this.destroyed || this.held) { return; }
      const items = this.list.nativeElement.querySelectorAll<HTMLElement>('[data-sonner-toast]');
      items.forEach(element => {
        const id = element.dataset.toastId;
        const toast = this.toasts.find(item => item.id === id);
        if (!toast || toast.removed) { return; }
        const previous = element.style.height;
        element.style.height = 'auto';
        const height = element.offsetHeight; // Layout height excludes the collapsed stack's scale transform.
        element.style.height = previous;
        this.heights.set(id, height);
        if (!toast.mounted) { this.toast.markMounted(id); }
      });
      this.rebuild();
    });
  }
}
